import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DateRangePicker } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { FileText, Pencil, ExternalLink, Copy, FileStack, MessagesSquare, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, History, Lock, Unlock, Send, CheckCircle, FileEdit } from 'lucide-react';
import HiloVersionesModal from './HiloVersionesModal';
import { cotizacionesAPI, tiposServicioAPI, alertasAPI, prefijosAPI } from '../services/api';
import { moneyFmt, formatDate, getEstadoColor, totalConRentabilidad, formatRelativeShort } from '../utils/helpers';
import { generatePdfCotizacion } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import { selectStyles } from '../utils/adminShared';
import { useConfirm, Tooltip } from './ui';
import { useModalKeyboard } from '../hooks/useModalKeyboard';

// Opciones de estado
const estadoOptions = [
    { value: '', label: 'Todos' },
    { value: 'borrador', label: 'Borrador' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' }
];

const estadoOptionsNoTodos = [
    { value: 'borrador', label: 'Borrador' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' }
];

const CotizacionesList = ({ onEdit, onNueva, configuracion }) => {
    const navigate = useNavigate();
    const { tienePermiso, user } = useAuth();
    // Bypass del bloqueo de estados finales: permiso explicito o superadmin.
    const puedeDesbloquear = tienePermiso('desbloquear_estado') || user?.rol === 'superadmin';
    const confirm = useConfirm();
    const verPdf = tienePermiso('ver_pdf');
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [filtros, setFiltros] = useState({
        estado: '',
        cliente: '',
        desde: '',
        hasta: '',
        prefijo_id: ''
    });
    // Prefijos activos, solo para el filtro cuando hay 2+.
    const [prefijosActivos, setPrefijosActivos] = useState([]);
    const [actividadModal, setActividadModal] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [hiloModal, setHiloModal] = useState(null); // id de la coti cuyo hilo se muestra

    // Paginacion client-side
    const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPaginas = Math.max(1, Math.ceil(cotizaciones.length / pageSize));
    const cotizacionesPaginadas = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return cotizaciones.slice(start, start + pageSize);
    }, [cotizaciones, currentPage, pageSize]);

    // Si cambian las cotizaciones o el pageSize y la pagina actual queda fuera, volver a 1
    useEffect(() => {
        if (currentPage > totalPaginas) setCurrentPage(1);
    }, [totalPaginas, currentPage]);

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    const abrirHistorial = async (cot) => {
        setActividadModal(cot);
        setLoadingHistorial(true);
        try {
            const res = await alertasAPI.getActividades(cot.id);
            setHistorial(res.data || []);
        } catch (err) {
            setHistorial([]);
        } finally {
            setLoadingHistorial(false);
        }
    };

    const cerrarHistorial = () => {
        setActividadModal(null);
        setHistorial([]);
    };

    useModalKeyboard({ open: !!actividadModal, onClose: cerrarHistorial });

    const handleDateRangeChange = (value) => {
        setDateRange(value);
        if (value && value[0] && value[1]) {
            setFiltros(f => ({
                ...f,
                desde: value[0].toISOString().split('T')[0],
                hasta: value[1].toISOString().split('T')[0]
            }));
        } else {
            setFiltros(f => ({ ...f, desde: '', hasta: '' }));
        }
    };

    const cargarCotizaciones = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtros.estado) params.estado = filtros.estado;
            if (filtros.cliente) params.cliente = filtros.cliente;
            if (filtros.desde) params.desde = filtros.desde;
            if (filtros.hasta) params.hasta = filtros.hasta;
            if (filtros.prefijo_id) params.prefijo_id = filtros.prefijo_id;

            const response = await cotizacionesAPI.getAll(params);
            setCotizaciones(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarCotizaciones();
        // Cargar prefijos activos para el filtro; silencioso si falla.
        prefijosAPI.getActivos()
            .then(res => setPrefijosActivos(res?.data || []))
            .catch(() => setPrefijosActivos([]));
    }, []);

    const handleFiltrar = (e) => {
        e.preventDefault();
        cargarCotizaciones();
    };

    const handleEliminar = async (cot) => {
        const tieneVersiones = (cot.versiones_count || 0) > 1;
        const ok = await confirm({
            title: '¿Eliminar cotización?',
            message: (
                <div>
                    <p>
                        Vas a eliminar la cotización <strong>{cot.numero}</strong>.
                        Esta acción no se puede deshacer.
                    </p>
                    {tieneVersiones && (
                        <div className="confirm-bullets" style={{ marginTop: 10 }}>
                            <div className="confirm-bullet">
                                <FileStack size={14} aria-hidden="true" className="confirm-bullet-icon" />
                                <span>
                                    Esta cotización es <strong>parte de un hilo de {cot.versiones_count} versiones</strong>.
                                    Si era la principal, <strong>la versión más reciente</strong> la reemplazará.
                                    Si no existe otra versión, se promoverá la <strong>raíz</strong>.
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            ),
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await cotizacionesAPI.delete(cot.id);
            toast.success(`Cotización ${cot.numero} eliminada`);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al eliminar: ' + err.message);
        }
    };

    const handleDuplicar = async (id) => {
        try {
            const response = await cotizacionesAPI.duplicate(id);
            toast.success(`Cotización duplicada: ${response.data.numero}`);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al duplicar: ' + err.message);
        }
    };

    const handleNuevaVersion = async (id) => {
        try {
            const response = await cotizacionesAPI.createVersion(id);
            toast.success(`Nueva versión creada: ${response.data.numero}`);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al crear versión: ' + err.message);
        }
    };

    const ESTADO_LABELS_CONFIRM = {
        borrador: 'Borrador',
        enviada: 'Enviada',
        aprobada: 'Aprobada',
        rechazada: 'Rechazada'
    };
    const handleCambiarEstado = async (cot, nuevoEstado) => {
        if (!cot || !nuevoEstado || cot.estado === nuevoEstado) return;

        const estadoAnteriorLabel = ESTADO_LABELS_CONFIRM[cot.estado] || cot.estado;
        const estadoNuevoLabel = ESTADO_LABELS_CONFIRM[nuevoEstado] || nuevoEstado;
        const saleDeBorrador = cot.estado === 'borrador' && nuevoEstado !== 'borrador';
        const vuelveABorrador = cot.estado !== 'borrador' && nuevoEstado === 'borrador';

        // Icono semantico del header del confirm segun estado destino.
        // Cuando es danger, ConfirmDialog ignora este icon y usa AlertTriangle propio.
        const ICONOS_ESTADO = {
            borrador: { Icon: FileEdit, variant: 'neutral' },
            enviada: { Icon: Send, variant: 'info' },
            aprobada: { Icon: CheckCircle, variant: 'success' }
        };
        const cfgIcon = ICONOS_ESTADO[nuevoEstado];
        const headerIcon = cfgIcon
            ? (
                <div className={`confirm-dialog-icon ${cfgIcon.variant}`}>
                    <cfgIcon.Icon size={20} aria-hidden="true" />
                </div>
            )
            : null;

        const ok = await confirm({
            title: `¿Cambiar estado a "${estadoNuevoLabel}"?`,
            icon: headerIcon,
            message: (
                <div>
                    <p style={{ marginBottom: 12 }}>
                        <strong>{cot.numero}</strong> pasará de{' '}
                        <strong>{estadoAnteriorLabel}</strong> a{' '}
                        <strong>{estadoNuevoLabel}</strong>.
                    </p>
                    <div className="confirm-bullets">
                        <div className="confirm-bullet">
                            <History size={14} aria-hidden="true" className="confirm-bullet-icon" />
                            <span>Quedará registrado en el historial de estados.</span>
                        </div>
                        {saleDeBorrador && (
                            <div className="confirm-bullet">
                                <Lock size={14} aria-hidden="true" className="confirm-bullet-icon" />
                                <span>La cotización <strong>ya no podrá editarse</strong> (solo modo detalle).</span>
                            </div>
                        )}
                        {vuelveABorrador && (
                            <div className="confirm-bullet">
                                <Unlock size={14} aria-hidden="true" className="confirm-bullet-icon" />
                                <span>La cotización volverá a ser editable.</span>
                            </div>
                        )}
                    </div>
                </div>
            ),
            confirmText: 'Cambiar estado',
            danger: nuevoEstado === 'rechazada'
        });
        if (!ok) return;

        try {
            await cotizacionesAPI.updateEstado(cot.id, nuevoEstado);
            cargarCotizaciones();
            toast.success(`Estado cambiado a "${estadoNuevoLabel}"`);
        } catch (err) {
            toast.error('Error al cambiar estado: ' + err.message);
        }
    };

    const handleVerPdf = async (id, preview = true) => {
        try {
            const response = await cotizacionesAPI.getById(id);
            // Si la cotizacion tiene archivo adjunto, usar ese en vez del PDF del sistema.
            if (response.data?.archivo_path) {
                if (preview) {
                    await cotizacionesAPI.verArchivo(id);
                } else {
                    await cotizacionesAPI.descargarArchivo(id);
                }
                return;
            }
            const { servicios: serviciosData, extras, ...cotizacionBase } = response.data;
            const tiposRes = await tiposServicioAPI.getAll();

            const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
            const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;

            // Preservar s.total de BD (ya viene con override + variaciones aplicadas
            // porque el submit del form recalcula toda la cadena antes de guardar).
            // Solo re-calculamos si el backend no lo trajo (defensa).
            const servicios = (serviciosData || []).map(s => {
                const asignaciones = (s.asignaciones || []).map(a => {
                    const horas = parseFloat(a.horas) || 0;
                    const costoHora = parseFloat(a.costo_hora) || 0;
                    const horasAdicionales = horas * 0.12;
                    return {
                        ...a,
                        horas_adicionales: horasAdicionales,
                        subtotal: (horas + horasAdicionales) * costoHora
                    };
                });

                const costoServicio = asignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                const total = parseFloat(s.total) || totalConRentabilidad(costoServicio, rentabilidad);
                return { ...s, asignaciones, costo: costoServicio, total };
            });

            // Calcular totales usando s.total de BD (con variaciones incluidas).
            // Respeta el flag incluye_igv (default TRUE) — cuando esta FALSE el IGV es 0.
            const subtotalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const subtotalExtras = (extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
            const subtotal = subtotalServicios;
            const aplicaIgv = cotizacionBase.incluye_igv !== false && cotizacionBase.incluye_igv !== 0;
            const igv = aplicaIgv ? subtotal * igvRate : 0;
            const total = subtotal + igv + subtotalExtras;

            const cotizacion = {
                ...cotizacionBase,
                subtotal,
                subtotalExtras,
                igv,
                total
            };

            await generatePdfCotizacion({
                cotizacion,
                servicios,
                extras,
                configuracion,
                tiposServicio: tiposRes.data || [],
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando cotizaciones...</div>;
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    return (
    <>
        <section className="card">
            <div className="cardHead">
                <h2 className="h2">Historial de Cotizaciones</h2>
                <div className="cardHead-actions">
                    <button className="btn ghost" onClick={cargarCotizaciones}>
                        Actualizar listado
                    </button>
                    <button className="btn neon" onClick={onNueva}>
                        + Nueva Cotización
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <form className="filtros-form" onSubmit={handleFiltrar}>
                <div className="filtros-grid">
                    <label className="field">
                        <span className="label">Buscar</span>
                        <input
                            className="input"
                            type="text"
                            value={filtros.cliente}
                            onChange={(e) => setFiltros(f => ({ ...f, cliente: e.target.value }))}
                            placeholder="Cliente, empresa o número..."
                        />
                    </label>

                    <div className="field">
                        <span className="label">Estado</span>
                        <Select
                            styles={selectStyles}
                            options={estadoOptions}
                            value={estadoOptions.find(opt => opt.value === filtros.estado) || estadoOptions[0]}
                            onChange={(selected) => setFiltros(f => ({ ...f, estado: selected?.value || '' }))}
                            placeholder="Todos"
                            isSearchable={false}
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>

                    {prefijosActivos.length > 1 && (
                        <div className="field">
                            <span className="label">Prefijo</span>
                            <Select
                                styles={selectStyles}
                                options={[
                                    { value: '', label: 'Todos' },
                                    ...prefijosActivos.map(p => ({ value: p.id, label: p.prefijo, color: p.color }))
                                ]}
                                value={
                                    filtros.prefijo_id
                                        ? { value: filtros.prefijo_id, label: (prefijosActivos.find(p => p.id === filtros.prefijo_id)?.prefijo) || filtros.prefijo_id }
                                        : { value: '', label: 'Todos' }
                                }
                                onChange={(selected) => setFiltros(f => ({ ...f, prefijo_id: selected?.value || '' }))}
                                placeholder="Todos"
                                isSearchable={false}
                            />
                        </div>
                    )}

                    <div className="field field-daterange">
                        <span className="label">Rango de fechas</span>
                        <DateRangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="dd/MM/yyyy"
                            character=" - "
                            placeholder="Seleccionar rango"
                            showOneCalendar
                            placement="bottomEnd"
                            className="rs-theme-dark"
                            ranges={[]}
                        />
                    </div>

                    <button className="btn ghost" type="submit">
                        Filtrar
                    </button>
                </div>
            </form>

            {/* Tabla de cotizaciones */}
            <div className="tableWrap">
                <table className="galTable">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Cliente</th>
                            <th>Fecha</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'center', width: '160px' }}>Actividad</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cotizaciones.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-message">
                                    No hay cotizaciones registradas
                                </td>
                            </tr>
                        ) : (
                            cotizacionesPaginadas.map((cot) => (
                                <tr key={cot.id}>
                                    <td className="strong">
                                        <div className="cot-numero-wrap">
                                            {cot.prefijo_color && (
                                                <Tooltip content={cot.prefijo_nombre || cot.prefijo_str}>
                                                    <span
                                                        className="prefijo-dot"
                                                        style={{ backgroundColor: cot.prefijo_color }}
                                                        aria-hidden="true"
                                                    />
                                                </Tooltip>
                                            )}
                                            <Tooltip content="Abrir cotización">
                                                <button
                                                    className="link-btn"
                                                    onClick={() => onEdit(cot.id)}
                                                    aria-label="Abrir cotización"
                                                >
                                                    {cot.numero}
                                                </button>
                                            </Tooltip>
                                            {cot.versiones_count > 1 && (
                                                <Tooltip content={`Ver ${cot.versiones_count} versiones para esta cotización`}>
                                                    <button
                                                        type="button"
                                                        className="versiones-badge"
                                                        onClick={() => setHiloModal(cot.id)}
                                                        aria-label={`Ver hilo de versiones (${cot.versiones_count})`}
                                                    >
                                                        <FileStack size={11} aria-hidden="true" />
                                                        {cot.versiones_count}
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div>{cot.cliente_nombre}</div>
                                        {cot.cliente_empresa && (
                                            <small className="text-muted">{cot.cliente_empresa}</small>
                                        )}
                                    </td>
                                    <td>{formatDate(cot.fecha)}</td>
                                    <td className="right strong">
                                        {(() => {
                                            const subtotalOpc = parseFloat(cot.subtotal_opcionales) || 0;
                                            const aplicaIgv = cot.incluye_igv !== false && cot.incluye_igv !== 0;
                                            const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
                                            const totalOpc = subtotalOpc + (aplicaIgv ? subtotalOpc * igvRate : 0);
                                            const totalProy = parseFloat(cot.total) || 0;
                                            const totalCombinado = totalProy + totalOpc;
                                            const hayOpc = subtotalOpc > 0;
                                            return (
                                                <>
                                                    <div>{moneyFmt(totalCombinado, cot.moneda)}</div>
                                                    {hayOpc && (
                                                        <div className="listado-desglose">
                                                            <span>Proyecto: {moneyFmt(totalProy, cot.moneda)}</span>
                                                            <span>Opcionales: {moneyFmt(totalOpc, cot.moneda)}</span>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </td>
                                    <td className="estado-cell">
                                        <Select
                                            styles={{
                                                ...selectStyles,
                                                control: (base, state) => ({
                                                    ...selectStyles.control(base, state),
                                                    borderColor: state.isFocused
                                                        ? 'rgba(140, 220, 255, 0.5)'
                                                        : getEstadoColor(cot.estado),
                                                    minHeight: '32px'
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: getEstadoColor(cot.estado),
                                                    padding: '4px',
                                                    '&:hover': {
                                                        color: 'rgba(140, 220, 255, 0.8)'
                                                    }
                                                })
                                            }}
                                            options={estadoOptionsNoTodos}
                                            value={estadoOptionsNoTodos.find(opt => opt.value === cot.estado)}
                                            onChange={(selected) => handleCambiarEstado(cot, selected?.value)}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            isDisabled={['aprobada', 'rechazada'].includes(cot.estado) && !puedeDesbloquear}
                                        />
                                    </td>
                                    <td>
                                        <div className="actividad-cell">
                                            <Tooltip content={
                                                cot.total_actividades > 0
                                                    ? `Ver historial de actividad (${cot.total_actividades} ${cot.total_actividades === 1 ? 'registro' : 'registros'})`
                                                    : 'Ver/registrar actividad'
                                            }>
                                                <button
                                                    className="btn-icon seguimiento"
                                                    onClick={() => abrirHistorial(cot)}
                                                    aria-label="Ver historial de actividad"
                                                >
                                                    <MessagesSquare size={16} />
                                                    {cot.total_actividades > 0 && (
                                                        <span className="actividad-count-badge">{cot.total_actividades}</span>
                                                    )}
                                                </button>
                                            </Tooltip>
                                            <div className="actividad-info">
                                                {cot.alerta_nombre && (
                                                    <Tooltip content={`Alerta: ${cot.alerta_nombre}`}>
                                                        <div className="actividad-alerta">
                                                            <span
                                                                className="alerta-dot"
                                                                style={{ background: cot.alerta_color || '#6b7280' }}
                                                                aria-hidden="true"
                                                            />
                                                            <span className="alerta-label">{cot.alerta_nombre}</span>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {cot.ultima_actividad_fecha && (
                                                    <Tooltip content={
                                                        cot.ultima_actividad_tipo
                                                            ? `${cot.ultima_actividad_tipo} · ${formatDate(cot.ultima_actividad_fecha)}`
                                                            : formatDate(cot.ultima_actividad_fecha)
                                                    }>
                                                        <div className="actividad-fecha">
                                                            {formatRelativeShort(cot.ultima_actividad_fecha)}
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="acciones-btns">
                                            {verPdf && (
                                                <Tooltip content="Previsualizar PDF">
                                                    <button
                                                        className="btn-icon pdf"
                                                        onClick={() => handleVerPdf(cot.id, true)}
                                                        aria-label="Previsualizar PDF"
                                                    >
                                                        <FileText size={16} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            {cot.estado === 'borrador' ? (
                                                <Tooltip content="Editar cotización">
                                                    <button
                                                        className="btn-icon edit"
                                                        onClick={() => onEdit(cot.id)}
                                                        aria-label="Editar cotización"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip content="Ver detalle completo">
                                                    <button
                                                        className="btn-icon detalle"
                                                        onClick={() => navigate(`/cotizaciones/${cot.id}/detalle`)}
                                                        aria-label="Ver detalle completo"
                                                    >
                                                        <ExternalLink size={16} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <Tooltip content="Duplicar como nueva cotización">
                                                <button
                                                    className="btn-icon duplicate"
                                                    onClick={() => handleDuplicar(cot.id)}
                                                    aria-label="Duplicar como nueva"
                                                >
                                                    <Copy size={16} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip content="Crear nueva versión">
                                                <button
                                                    className="btn-icon version"
                                                    onClick={() => handleNuevaVersion(cot.id)}
                                                    aria-label="Crear nueva versión"
                                                >
                                                    <FileStack size={16} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip content="Eliminar cotización">
                                                <button
                                                    className="btn-icon delete"
                                                    onClick={() => handleEliminar(cot)}
                                                    aria-label="Eliminar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="table-footer">
                <span className="total-count">
                    {cotizaciones.length === 0
                        ? 'Sin cotizaciones'
                        : `Mostrando ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, cotizaciones.length)} de ${cotizaciones.length}`}
                </span>

                {cotizaciones.length > 0 && (
                    <div className="pagination">
                        <label className="pagination-size">
                            <span>Por página:</span>
                            <select
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            >
                                {PAGE_SIZE_OPTIONS.map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </label>

                        <div className="pagination-controls">
                            <Tooltip content="Primera página">
                                <button
                                    type="button"
                                    className="btn-icon pagination-btn"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    aria-label="Primera página"
                                >
                                    <ChevronsLeft size={16} />
                                </button>
                            </Tooltip>
                            <Tooltip content="Anterior">
                                <button
                                    type="button"
                                    className="btn-icon pagination-btn"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    aria-label="Página anterior"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                            </Tooltip>
                            <span className="pagination-pages">
                                Página <strong>{currentPage}</strong> de {totalPaginas}
                            </span>
                            <Tooltip content="Siguiente">
                                <button
                                    type="button"
                                    className="btn-icon pagination-btn"
                                    onClick={() => setCurrentPage(p => Math.min(totalPaginas, p + 1))}
                                    disabled={currentPage === totalPaginas}
                                    aria-label="Página siguiente"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </Tooltip>
                            <Tooltip content="Última página">
                                <button
                                    type="button"
                                    className="btn-icon pagination-btn"
                                    onClick={() => setCurrentPage(totalPaginas)}
                                    disabled={currentPage === totalPaginas}
                                    aria-label="Última página"
                                >
                                    <ChevronsRight size={16} />
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                )}
            </div>

        </section>

            {/* Modal de historial de seguimiento */}
            {actividadModal && (
                <div className="modal-overlay" onClick={cerrarHistorial}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }} role="dialog" aria-modal="true">
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0 }}>Seguimiento</h3>
                                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                    {actividadModal.numero} - {actividadModal.cliente_nombre}
                                </span>
                            </div>
                            <button className="modal-close" onClick={cerrarHistorial}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <h4 style={{ color: 'var(--text)', marginBottom: '8px', marginTop: 0 }}>Historial de actividades</h4>
                            {loadingHistorial ? (
                                <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Cargando...</p>
                            ) : historial.length === 0 ? (
                                <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Sin actividades registradas</p>
                            ) : (
                                <div className="actividad-historial">
                                    {historial.map(act => (
                                        <div key={act.id} className="actividad-item">
                                            <div className="actividad-item-dot" />
                                            <div className="actividad-item-content">
                                                <div className="actividad-item-tipo">{act.tipo_nombre}</div>
                                                {act.descripcion && (
                                                    <div className="actividad-item-desc">{act.descripcion}</div>
                                                )}
                                                <div className="actividad-item-meta">
                                                    {act.usuario_nombre} - {new Date(act.created_at).toLocaleDateString('es-PE')} {new Date(act.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <HiloVersionesModal
                open={!!hiloModal}
                cotizacionId={hiloModal}
                onClose={() => setHiloModal(null)}
                onChange={cargarCotizaciones}
            />
    </>
    );
};

export default CotizacionesList;
